import { createFeatureSelector, createSelector } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { FeatureFlagService } from '@uzurv/rideWss/app/services';
import { AppInjector, isNullOrUndefined, OrganizationRiderListItemUtil, RideUtil } from '@uzurv/rideWss/app/util';
import {
  OrganizationServiceLevel,
  PaymentCollectionConstants,
  RideMetadataField,
  RideMetadataValue,
  RideRequestTypeConstants,
  ServiceLevelConstants,
  TripListItem
} from '@uzurv/rideWss/clara';
import { findIndex } from 'lodash';
import moment from 'moment-timezone';
import * as PaymentSelectors from '../payment/payment.selectors';
import { LocationTypes, RidesState } from './rides.reducer';

export const ridesState = createFeatureSelector<RidesState>('rides');

export const getIsFetchingOrganizationRiders = createSelector(
  ridesState,
  (state: RidesState) => state.isFetchingOrganizationRiderListItems
);

export const getIsInitialOrganizationRiderListItemsLoaded = createSelector(
  ridesState,
  (state: RidesState) => state.isInitialOrganizationRiderListItemsLoaded
);

export const getOrganizationRiders = createSelector(ridesState, (state: RidesState) => state.organizationRiderListItems);

export const getItineraryCreationEnabledOrganizationRiders = createSelector(ridesState, (state: RidesState) =>
  state.organizationRiderListItems.filter(OrganizationRiderListItemUtil.isItineraryCreationEnabled).sort((a, b) => {
    return a.organizationName.localeCompare(b.organizationName);
  })
);

export const getUnverifiedOrganizationRiders = createSelector(ridesState, (state: RidesState) => {
  return state.organizationRiderListItems.filter(orli => !orli.proofed).concat(state.matchingUnclaimedOrganizationRiderListItems);
});

export const getOrganizationRiderById = (organizationRiderId: number) =>
  createSelector(ridesState, (state: RidesState) => {
    return state.organizationRiderListItems?.find(organizationRiderListItem => {
      return organizationRiderListItem.organizationRiderId === organizationRiderId;
    });
  });

export const isRiderEligible = createSelector(ridesState, (state: RidesState) =>
  state.organizationRiderListItems.some(orli => orli.eligible)
);

export const getOrganizationsString = createSelector(ridesState, (state: RidesState) => {
  let organizationNames: string = '';
  for (let i = 0; i < state.organizationRiderListItems?.length; i++) {
    organizationNames += state.organizationRiderListItems[i].organizationName;
    if (i < state.organizationRiderListItems.length - 1) {
      organizationNames += ', ';
    }
  }
  return organizationNames.length > 0 ? organizationNames : null;
});

export const getRiderAppContactInformationUpdatesEnabled = createSelector(ridesState, (state: RidesState) => {
  // TODO: Revisit rider contact information with the distinction between the rider's uzurv profile and organization profiles
  return false;
});

export const getOrganizationRiderServiceLevels = createSelector(ridesState, (state: RidesState) => {
  return state.organizationRiderServiceLevels;
});

export const getOrganizationServiceLevelByCode = (serviceLevelCode: string) =>
  createSelector(ridesState, (state: RidesState) => {
    return state.selectedOrganizationRiderListItem.organizationServiceLevels.find(osl => {
      return osl.serviceLevel.code === serviceLevelCode;
    });
  });

export const getOrganizationRiderServiceLevelByCode = (serviceLevelCode: string) =>
  createSelector(ridesState, (state: RidesState) => {
    return state.organizationRiderServiceLevels.find(orsl => {
      return orsl.serviceLevel.code === serviceLevelCode;
    });
  });

export const getAvailableProviders = createSelector(ridesState, (state: RidesState) => {
  return state.availableProviders;
});

export const getIsCreateRideEnabled = createSelector(ridesState, (state: RidesState) => {
  return state.organizationRiderListItems?.some(OrganizationRiderListItemUtil.isItineraryCreationEnabled);
});

export const getIsRideNowEnabledForSelectedOrganizationRider = createSelector(ridesState, (state: RidesState) => {
  // TODO: Uncomment this when service level is selected first

  // if (isNullOrUndefined(state.selectedOrganizationServiceLevel)) {
  //   return false;
  // }

  // return state.selectedOrganizationServiceLevel.rideNowEnabled;
  return state.selectedOrganizationRiderListItem?.organizationServiceLevels.some(osl => osl.rideNowEnabled);
});

export const getIsWillCallEnabledForSelectedOrganizationRider = createSelector(ridesState, (state: RidesState) => {
  // TODO: Uncomment this when service level is selected first

  // if (isNullOrUndefined(state.selectedOrganizationServiceLevel)) {
  //   return false;
  // }

  // return state.selectedOrganizationServiceLevel.willCallEnabled;

  return state.selectedOrganizationRiderListItem?.organizationServiceLevels?.some(osl => osl.willCallEnabled);
});

export const getIsScheduledEnabledForSelectedOrganizationRider = createSelector(ridesState, (state: RidesState) => {
  // TODO: Uncomment this when service level is selected first

  // if (isNullOrUndefined(state.selectedOrganizationServiceLevel)) {
  //   return false;
  // }

  // return state.selectedOrganizationServiceLevel.willCallEnabled;

  return state.selectedOrganizationRiderListItem?.organizationServiceLevels?.some(osl => osl.scheduledEnabled);
});

export const getIsRiderPaymentRequired = createSelector(ridesState, (state: RidesState) => {
  return state.selectedOrganizationRiderListItem?.riderPaymentRequired;
});

export const getIsRiderPaymentAccountEnabled = createSelector(ridesState, (state: RidesState) => {
  return state.selectedOrganizationRiderListItem?.riderPaymentAccountEnabled;
});

export const getIsRiderFareCollectionEnabled = createSelector(ridesState, (state: RidesState) => {
  return state.selectedOrganizationRiderListItem?.riderFareCollectionEnabled;
});

export const getMinimumMinutesBeforeScheduledRideAllowed = createSelector(ridesState, (state: RidesState) => {
  // TODO: Uncomment this when service level is selected first

  // if (isNullOrUndefined(state.selectedOrganizationServiceLevel)) {
  //   return 120;
  // }

  // return state.selectedOrganizationServiceLevel.minimumMinutesBeforeScheduledRideAllowed;

  // Default for now
  if (isNullOrUndefined(state.selectedOrganizationRiderListItem?.organizationServiceLevels)) {
    return 4 * 60;
  }

  return state.selectedOrganizationRiderListItem.organizationServiceLevels[0].minimumMinutesBeforeScheduledRideAllowed;
});

export const getIsCurrentRideRequestLegRideBackupOptional = createSelector(ridesState, (state: RidesState) => {
  if (isNullOrUndefined(state.currentRideRequestLeg)) {
    return false;
  }

  if (isNullOrUndefined(state.selectedOrganizationRiderListItem)) {
    return false;
  }

  if (isNullOrUndefined(state.organizationRiderServiceLevels)) {
    return false;
  }

  return RideUtil.isRideRequestRideBackupEligible(
    state.currentRideRequestLeg,
    state.currentRideRequestLeg.rideRequestInfo,
    state.selectedOrganizationRiderListItem.riderPaymentRequired,
    state.selectedOrganizationRiderListItem.riderPaymentAccountEnabled,
    state.selectedOrganizationRiderListItem.organizationServiceLevels,
    state.organizationRiderServiceLevels
  );
});

export const getIsSelectedRideRideBackupAccepted = createSelector(ridesState, (state: RidesState) => {
  return state.selectedRide?.minimumServiceLevel.code === ServiceLevelConstants.AMBULATORY_CURB_TO_CURB_CODE;
});

export const getIsRideBackupDisabled = createSelector(ridesState, (state: RidesState) => {
  if (isNullOrUndefined(state.currentRideRequestLeg)) {
    return true;
  }

  if (isNullOrUndefined(state.currentRideRequestLeg.riderPaymentCollectionType?.code === PaymentCollectionConstants.FARE_COLLECTION)) {
    return true;
  }

  // Check whether the backup service level is optional
  const ac2cOrganizationServiceLevel: OrganizationServiceLevel = state.selectedOrganizationRiderListItem?.organizationServiceLevels.find(
    osl => osl.serviceLevel.code === ServiceLevelConstants.AMBULATORY_CURB_TO_CURB_CODE
  );

  if (!isNullOrUndefined(ac2cOrganizationServiceLevel) && !ac2cOrganizationServiceLevel.optional) {
    return true;
  }

  return false;
});

export const getIsRideBackupAccepted = createSelector(ridesState, (state: RidesState) => {
  return state.currentRideRequestLeg?.rideBackupAccepted;
});

export const getUpcomingRides = createSelector(ridesState, (state: RidesState) => state.upcomingRideListItems);

export const getUpcomingRideGroups = createSelector(ridesState, (state: RidesState) => state.upcomingRideGroups);

export const getInitialUpcomingRidesLoading = createSelector(ridesState, (state: RidesState) => state.initialUpcomingRidesLoading);

export const getAdditionalUpcomingRidesLoading = createSelector(ridesState, (state: RidesState) => state.additionalUpcomingRidesLoading);

export const getUpcomingRidesRefreshing = createSelector(ridesState, (state: RidesState) => state.upcomingRidesRefreshing);

export const getUpcomingRidesInfiniteScrollEnabled = createSelector(
  ridesState,
  (state: RidesState) => state.upcomingRideListItemsInfiniteScrollEnabled
);

export const getPastRides = createSelector(ridesState, (state: RidesState) => state.pastRideListItems);

export const getPastRideGroups = createSelector(ridesState, (state: RidesState) => state.pastRideGroups);

export const getInitialPastRidesLoading = createSelector(ridesState, (state: RidesState) => state.initialPastRidesLoading);

export const getAdditionalPastRidesLoading = createSelector(ridesState, (state: RidesState) => state.additionalPastRidesLoading);

export const getPastRidesRefreshing = createSelector(ridesState, (state: RidesState) => state.pastRidesRefreshing);

export const getPastRidesInfiniteScrollEnabled = createSelector(
  ridesState,
  (state: RidesState) => state.pastRideListItemsInfiniteScrollEnabled
);

export const getActiveRide = createSelector(ridesState, (state: RidesState) => state.activeRide);

export const getSelectedRide = createSelector(ridesState, (state: RidesState) => state.selectedRide);

export const getRideRequestInfo = createSelector(ridesState, (state: RidesState) => state.currentRideRequestLeg?.rideRequestInfo);

export const getEstimatedDropoffTime = createSelector(ridesState, (state: RidesState) => {
  return state.currentRideRequestLeg.rideRequestInfo.estimatedDropoffDate;
});

export const getSelectedRideEstimate = createSelector(ridesState, (state: RidesState) => state.selectedRideEstimate);

export const getRideDetailLoading = createSelector(ridesState, (state: RidesState) => state.rideDetailLoading);

export const getDisableCancel = createSelector(ridesState, (state: RidesState) => state.disableCancel);

export const getDisableSubmit = createSelector(ridesState, (state: RidesState) => state.disableSubmit);

export const getFavoriteLocations = createSelector(ridesState, (state: RidesState) => state.favoriteLocationListItems);

export const getFavoriteLocationByLabel = createSelector(ridesState, (state: RidesState, props: { label: string }) => {
  return state.favoriteLocationListItems.find(favoriteLocation => {
    return favoriteLocation.label === props.label;
  });
});

export const getFavoriteLocationByLocationId = createSelector(ridesState, (state: RidesState, props: { locationId: number }) => {
  return state.favoriteLocationListItems.find(favoriteLocation => {
    return favoriteLocation.location.id === props.locationId;
  });
});

export const getSelectedFavoriteLocationSearchResult = createSelector(
  ridesState,
  (state: RidesState) => state.selectedFavoriteLocationSearchResult
);

export const getHomeLocation = createSelector(ridesState, (state: RidesState) => {
  return state.favoriteLocationListItems.find(favoriteLocation => {
    return favoriteLocation.label === 'Home';
  });
});

export const getRecentLocations = createSelector(ridesState, (state: RidesState) => state.recentLocationListItems);

export const getFavoriteLocationsLoading = createSelector(ridesState, (state: RidesState) => state.favoriteLocationsLoading);

export const getAddFavoriteLocationLoading = createSelector(ridesState, (state: RidesState) => state.addFavoriteLocationLoading);

export const getRecentLocationsLoading = createSelector(ridesState, (state: RidesState) => state.recentLocationsLoading);

export const getItinerary = createSelector(ridesState, (state: RidesState) => state.itinerary);

export const getCreateItineraryUIState = createSelector(ridesState, (state: RidesState) => state.createItineraryUIState);

export const getCurrentRideRequestLeg = createSelector(ridesState, (state: RidesState) => state.currentRideRequestLeg);

export const getCurrentRideRequestLegRiderFeeEstimate = createSelector(
  ridesState,
  (state: RidesState) => state.currentRideRequestLeg?.riderFeeEstimate
);

export const getLatestItineraryRideRequestLeg = createSelector(ridesState, (state: RidesState) => {
  if (isNullOrUndefined(state.itinerary)) {
    return null;
  }
  if (isNullOrUndefined(state.itinerary.rideRequests)) {
    return null;
  }
  if (state.itinerary.rideRequests.length === 0) {
    return null;
  }
  return state.itinerary.rideRequests[state.itinerary.rideRequests.length - 1];
});

export const getAutocompleteListItems = createSelector(ridesState, (state: RidesState) => state.autoCompleteListItems);

export const getAutocompleteListItemsForLocationType = (locationType: LocationTypes) =>
  createSelector(ridesState, (state: RidesState) =>
    state.autoCompleteListItemsLocationType === locationType ? state.autoCompleteListItems : []
  );

export const getRideListItemIndex = createSelector(ridesState, (state: RidesState, props) => {
  return state.upcomingRideListItems.findIndex(rideListItem => rideListItem.rideRequestId === props.rideRequestId);
});

export const getRideRequestTypeByCode = createSelector(ridesState, (state: RidesState, props) => {
  return state.rideRequestTypes.find(rrt => props.code === rrt.code);
});

export const getItineraryCounterString = (rideListItem: TripListItem) =>
  createSelector(ridesState, (state: RidesState) => {
    const itineraryGroup: Array<TripListItem> = state.upcomingRideListItems.filter(rli => rideListItem.itineraryId === rli.itineraryId);
    const itineraryLength: number = itineraryGroup.length;
    if (itineraryLength < 2) {
      return null;
    }

    const index =
      findIndex(itineraryGroup, (rli: TripListItem) => {
        return rideListItem.rideRequestId === rli.rideRequestId;
      }) + 1;

    const translateService: TranslateService = AppInjector.get(TranslateService);
    return translateService.instant('itineraryLegCounter', { legIndex: index, totalLegs: itineraryLength });
  });

export const getReservationIdByRideRequestId = createSelector(ridesState, (state: RidesState, props) => {
  const tripListItem: TripListItem = state.upcomingRideListItems.find(rli => props.rideRequestId === rli.rideRequestId);
  return !isNullOrUndefined(tripListItem) ? tripListItem.reservationId : null;
});

export const getSelectedPaymentCollectionType = createSelector(ridesState, (state: RidesState) => state.selectedPaymentCollectionType);

export const getSelectedPaymentAccount = createSelector(ridesState, (state: RidesState) => state.selectedPaymentAccount);

export const getIsNewRideRequestValid = createSelector(ridesState, (state: RidesState) => {
  // Null Checks
  if (
    isNullOrUndefined(state.itinerary) ||
    isNullOrUndefined(state.currentRideRequestLeg) ||
    isNullOrUndefined(state.currentRideRequestLeg.pickupLocation) ||
    isNullOrUndefined(state.currentRideRequestLeg.dropoffLocation) ||
    isNullOrUndefined(state.currentRideRequestLeg.rideRequestType) ||
    isNullOrUndefined(state.currentRideRequestLeg.rideRequestInfo) ||
    (isNullOrUndefined(state.currentRideRequestLeg.pickupDate) && !state.currentRideRequestLeg.rideRequestType.onDemand) ||
    isNullOrUndefined(state.selectedServiceLevelType)
  ) {
    return false;
  }

  // rideMetadataFields can be optional, don't include in the validation.
  const requiredRideMetadataValues: Array<RideMetadataValue> = state.currentRideRequestLeg.rideMetadataValues.filter(
    rideMetadataValue => rideMetadataValue.rideMetadataField.required && !isNullOrUndefined(rideMetadataValue.value)
  );
  const requiredMetadataFields: Array<RideMetadataField> = state.rideMetadataFields.filter(rideMetadataField => rideMetadataField.required);

  // Should be an equal number of requiredRideMetadataValues and requiredMetadataFields.
  if (requiredRideMetadataValues.length !== requiredMetadataFields.length) {
    return false;
  }

  // Past Pickup Date
  if (!state.isRideSubscription && moment(state.currentRideRequestLeg.pickupDate).isBefore(moment())) {
    return false;
  }

  // Outside the scheduling window
  if (!isNullOrUndefined(state.selectedOrganizationRiderListItem.organizationServiceLevels[0].minimumMinutesBeforeScheduledRideAllowed)) {
    if (
      moment(state.currentRideRequestLeg.pickupDate).isBefore(
        moment().add(
          state.selectedOrganizationRiderListItem.organizationServiceLevels[0].minimumMinutesBeforeScheduledRideAllowed,
          'minutes'
        )
      )
    ) {
      return false;
    }
  }

  // Payment Collection
  if (state.selectedOrganizationRiderListItem.riderPaymentRequired) {
    if (isNullOrUndefined(state.currentRideRequestLeg.riderPaymentCollectionType)) {
      return false;
    }

    if (state.currentRideRequestLeg.riderPaymentCollectionType.code === PaymentCollectionConstants.PAYMENT_ACCOUNT) {
      if (isNullOrUndefined(state.selectedPaymentAccount)) {
        return false;
      }

      const featureFlagService: FeatureFlagService = AppInjector.get(FeatureFlagService);

      if (featureFlagService.isCreditCardValidationEnabled() && !state.selectedPaymentAccount.userVerified) {
        return false;
      }

      const expirationMoment = moment(state.selectedPaymentAccount.cardExpiration, 'YYYY-MM').endOf('month');
      if (moment(state.itinerary.pickupDate).isAfter(expirationMoment)) {
        return false;
      }
    }
  }

  // Preferred Provider
  if (state.selectedOrganizationRiderListItem.preferredProviderEnabled) {
    if (isNullOrUndefined(state.currentRideRequestLeg.preferredProviderId)) {
      return false;
    }
  }

  return true;
});

export const getRiderHasPca = createSelector(ridesState, (state: RidesState) => state.riderHasPca);

export const getIsRideSubscription = createSelector(ridesState, (state: RidesState) => state.isRideSubscription);

export const getSelectedDaysOfTheWeek = createSelector(ridesState, (state: RidesState) => state.selectedDaysOfTheWeek);

export const getSubscriptionStartDate = createSelector(ridesState, (state: RidesState) => state.subscriptionStartDate);

export const getSubscriptionEndDate = createSelector(ridesState, (state: RidesState) => state.subscriptionEndDate);

export const getRideSubscriptionLegs = createSelector(ridesState, (state: RidesState) => state.itinerary?.rideRequests);

export const getRideMetadataFields = createSelector(ridesState, (state: RidesState) => {
  return state.rideMetadataFields;
});

export const getIsEditingSubscriptionDetails = createSelector(ridesState, (state: RidesState) => state.isEditingSubscriptionDetails);

export const listMatchingUnclaimedOrganizationRiders = createSelector(
  ridesState,
  (state: RidesState) => state.matchingUnclaimedOrganizationRiderListItems
);

export const getSelectedOrganizationRiderListItem = createSelector(
  ridesState,
  (state: RidesState) => state.selectedOrganizationRiderListItem
);

export const getSelectedOrganizationRiderServiceLevels = createSelector(
  getSelectedOrganizationRiderListItem,
  selectedOrganizationRiderListItem => selectedOrganizationRiderListItem?.organizationServiceLevels
);

export const getSelectedServiceLevelType = createSelector(ridesState, (state: RidesState) => state.selectedServiceLevelType);

export const getSelectedServiceLevel = createSelector(ridesState, (state: RidesState) => state.currentRideRequestLeg?.serviceLevel);

export const getSelectedOrganizationServiceLevel = createSelector(ridesState, (state: RidesState) =>
  state.selectedOrganizationRiderListItem?.organizationServiceLevels.find(
    serviceLevel => serviceLevel.serviceLevel.id === state.currentRideRequestLeg?.serviceLevel?.id
  )
);

export const isBasicEnabled = createSelector(getSelectedOrganizationRiderServiceLevels, organizationServiceLevels =>
  organizationServiceLevels?.some(
    osl =>
      osl.serviceLevel.code === ServiceLevelConstants.AMBULATORY_CURB_TO_CURB_CODE ||
      osl.serviceLevel.code === ServiceLevelConstants.AMBULATORY_DOOR_TO_DOOR_CODE
  )
);

export const isWheelchairEnabled = createSelector(getSelectedOrganizationRiderServiceLevels, organizationServiceLevels =>
  organizationServiceLevels?.some(
    osl =>
      osl.serviceLevel.code === ServiceLevelConstants.NON_AMBULATORY_CURB_TO_CURB_CODE ||
      osl.serviceLevel.code === ServiceLevelConstants.NON_AMBULATORY_DOOR_TO_DOOR_CODE
  )
);

export const isScheduledEnabled = createSelector(
  getSelectedOrganizationServiceLevel,
  selectedOrganizationServiceLevel => selectedOrganizationServiceLevel?.scheduledEnabled
);

export const isRideNowSelected = createSelector(getCurrentRideRequestLeg, (currentRideRequestLeg) =>
  currentRideRequestLeg?.rideRequestType?.code === RideRequestTypeConstants.RIDE_NOW
);

export const isWillCallSelected = createSelector(getCurrentRideRequestLeg, (currentRideRequestLeg) =>
  currentRideRequestLeg?.rideRequestType?.code === RideRequestTypeConstants.WILL_CALL
);

export const isRideNowEnabled = createSelector(
  getSelectedOrganizationServiceLevel,
  selectedOrganizationServiceLevel => selectedOrganizationServiceLevel?.rideNowEnabled
);

export const isVehicleSelectionUnavailable = createSelector(
  isBasicEnabled,
  isWheelchairEnabled,
  (isBasicEnabled, isWheelchairEnabled) => !isBasicEnabled || !isWheelchairEnabled
);

export const isRideRequestTypeSelectionUnavailable = createSelector(
  getSelectedOrganizationServiceLevel,
  isScheduledEnabled,
  isRideNowEnabled,
  (selectedOrganizationServiceLevel, isScheduledEnabled, isRideNowEnabled) =>
    !isNullOrUndefined(selectedOrganizationServiceLevel) && (!isScheduledEnabled || !isRideNowEnabled)
);

export const isActionInProgress = createSelector(ridesState, (state: RidesState) => state.isActionInProgress);

export const getCombinedOrganizationRiderListItems = createSelector(ridesState, (state: RidesState) => {
  const combinedOrganizationRiderListItems = state.organizationRiderListItems.concat(state.matchingUnclaimedOrganizationRiderListItems);

  // Sort items by proofed status.
  combinedOrganizationRiderListItems.sort((a, b) => {
    return (a.proofed === b.proofed ? 0 : a.proofed ? -1 : 1) || a.organizationName.localeCompare(b.organizationName);
  });

  return combinedOrganizationRiderListItems;
});

// TODO: Consider a composite selector class to avoid circular dependencies.
// Add additional selectors here as we need more data to determine if the create itinerary button should be enabled.
export const isCreateItineraryDataLoaded = createSelector(
  getIsInitialOrganizationRiderListItemsLoaded,
  PaymentSelectors.isOutstandingChargesLoaded,
  (isInitialOrganizationRiderListItemsLoaded, isOutstandingChargesLoaded) => {
    return isInitialOrganizationRiderListItemsLoaded && isOutstandingChargesLoaded;
  }
);

export const getDefaultOrganizationBookingPhoneNumber = createSelector(ridesState, (state: RidesState) => {
  if (isNullOrUndefined(state.organizationRiderListItems) || state.organizationRiderListItems.length === 0) {
    return null;
  }

  return state.organizationRiderListItems[0].bookingPhoneNumber;
});

export const isFetchingUpcomingRideListItems = createSelector(ridesState, (state: RidesState) => state.fetchingUpcomingRideListItems);
